import React, { useCallback, useEffect, useState } from "react";
import Axios from "axios";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Config from "../Config";
import {
  hasErrors,
  invoiceErrors,
  invoiceState,
  isValidBelgianVat,
  normalizeBelgianVat,
  prefill,
} from "../../utils/peppol";
import { Icon, Sprite } from "../User/Orders/icons";
import "../User/Orders/orders.css";
import "./invoice.css";

/**
 * Demander une facture PEPPOL — « PEPPOL et suivi plein ecran.dc.html »,
 * écrans F01 à F07, règles de app/(profile)/peppol-invoice.tsx du mobile.
 *
 * Deux entrées, un seul écran :
 *  - /orders/:id/invoice, dans l'app (onglet Passées) : retour aux
 *    commandes, « Commande WOSH-… » en surtitre, profil pré-rempli et
 *    mis à jour après la demande, comme sur mobile ;
 *  - /invoice/:token, page publique (le jeton suffit) : sans compte.
 *    /request-invoice/:token, l'adresse des mails du backend, y mène aussi.
 *
 *   GET  /api/v1/public_invoices/:token
 *   POST /api/v1/public_invoices/:token  { public_invoice: {...} }
 */

const baseUrl = () => (Config.authURL || Config.apiURL || "").replace(/\/$/, "");

/* La route /api/dev de src/setupProxy.js n'existe qu'en local. */
const isDevOdooRoute = () =>
  process.env.NODE_ENV === "development" &&
  process.env.REACT_APP_USE_STAGING_PUBLIC_INVOICE_POST !== "true";

/** La réponse arrive sous plusieurs formes selon la version du backend. */
const payloadOf = (response) => {
  const data = response?.data;
  if (data?.public_invoice && data?.order) {
    return {
      ...data.order,
      ...data.public_invoice,
      user: data.public_invoice.user || data.order.user || data.user,
    };
  }
  return data?.public_invoice || data?.invoice || data?.order || data?.data || data;
};

/** Message lisible d'un refus du serveur, sinon null. */
const serverMessage = (error) => {
  const data = error?.response?.data;
  if (typeof data === "string") return data;
  if (Array.isArray(data?.errors)) return data.errors.join(", ");
  if (typeof data?.errors === "string") return data.errors;
  if (typeof data?.error === "string") return data.error;
  if (typeof data?.message === "string") return data.message;
  return null;
};

const authHeaders = (authUser) => ({
  uid: authUser?.user_uid,
  client: authUser?.user_client,
  "access-token": authUser?.user_access_token,
});

export function InvoiceRequest({ token, orderLabel, authUser, onBack, onDone }) {
  const { t } = useTranslation("app");
  const p = (key) => t(`peppolInvoice.${key}`);

  const [status, setStatus] = useState(token ? "loading" : "error");
  const [state, setState] = useState("form");
  const [invoiceRef, setInvoiceRef] = useState(null);
  const [form, setForm] = useState({ company: "", vat: "", email: "", address: "" });
  const [touched, setTouched] = useState({});
  const [attempted, setAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const load = useCallback(async () => {
    if (!token) {
      setStatus("error");
      return;
    }
    setStatus("loading");
    try {
      const [response, profile] = await Promise.all([
        Axios({ url: `${baseUrl()}/api/v1/public_invoices/${token}`, method: "GET" }),
        authUser?.user_uid
          ? Axios({ url: `${Config.apiURL}/customer/profiles`, method: "GET", headers: authHeaders(authUser) })
              .then((r) => r?.data?.profile || {})
              .catch(() => ({}))
          : Promise.resolve({}),
      ]);
      const invoice = payloadOf(response);
      if (!invoice || typeof invoice !== "object") throw new Error("empty");
      setForm(prefill(profile, invoice));
      setState(invoiceState(invoice));
      setInvoiceRef(invoice.odoo_invoice_id ?? null);
      setStatus("ready");
    } catch (error) {
      setStatus("error");
    }
  }, [token, authUser]);

  useEffect(() => {
    load();
  }, [load]);

  const errors = invoiceErrors(form);
  const errorFor = (key) => ((touched[key] || attempted) && errors[key] ? p(errors[key]) : null);
  // Valeur lue pendant l'événement : l'updater peut s'exécuter plus tard.
  const set = (key) => (event) => {
    const { value } = event.target;
    setForm((current) => ({ ...current, [key]: value }));
  };
  const touch = (key) => () => setTouched((current) => ({ ...current, [key]: true }));

  const submit = async (event) => {
    event.preventDefault();
    // Le bouton reste actif : un clic sur un formulaire invalide dit pourquoi.
    if (hasErrors(errors)) {
      setAttempted(true);
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    setSubmitError(null);

    const fields = {
      company_name: form.company.trim(),
      vat: normalizeBelgianVat(form.vat),
      email: form.email.trim(),
      invoicing_address: form.address.trim(),
    };

    try {
      if (isDevOdooRoute()) {
        await Axios({ url: "/api/dev/odoo-public-invoice", method: "POST", data: { secure_token: token, ...fields } });
      } else {
        await Axios({
          url: `${baseUrl()}/api/v1/public_invoices/${token}`,
          method: "POST",
          data: { public_invoice: { token, ...fields } },
        });
      }
    } catch (error) {
      setSubmitError((error?.response?.status === 422 && serverMessage(error)) || p("somethingWrong"));
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    setState("success");

    // Comme le mobile : le profil garde ces coordonnées pour la prochaine fois.
    if (authUser?.user_uid) {
      Axios({
        url: `${Config.authURL}/auth`,
        method: "PUT",
        headers: authHeaders(authUser),
        data: { company_name: fields.company_name, vat: fields.vat, invoicing_address: fields.invoicing_address },
      }).catch(() => {});
    }
  };

  const field = (key, label, props = {}) => {
    const error = errorFor(key);
    return (
      <label className="wosh-inv__field" htmlFor={`wosh-inv-${key}`}>
        <span className="wosh-inv__label">{label}</span>
        <input
          id={`wosh-inv-${key}`}
          className={`wosh-inv__input${error ? " wosh-inv__input--error" : ""}`}
          value={form[key]}
          onChange={set(key)}
          onBlur={touch(key)}
          aria-invalid={Boolean(error)}
          {...props}
        />
        {error && <span className="wosh-inv__error">{error}</span>}
      </label>
    );
  };

  return (
    <div className="wosh-inv">
      <header className="wosh-od__head">
        {onBack && (
          <button type="button" className="wosh-od__back" onClick={onBack} aria-label={p("goBack")}>
            <Icon name="arrow-back" />
          </button>
        )}
        <div className="wosh-ord__titles">
          {orderLabel && <div className="wosh-ord__eyebrow">{orderLabel}</div>}
          <h1 className="wosh-inv__title">{p("headerTitle")}</h1>
        </div>
      </header>

      {status === "loading" ? (
        <div className="wosh-od__state" aria-busy="true">
          <span className="wosh-ord__spinner" />
        </div>
      ) : status === "error" ? (
        <div className="wosh-od__state">
          <div className="wosh-od__state-icon wosh-od__state-icon--muted">
            <Icon name="cloud-offline-outline" />
          </div>
          <div className="wosh-od__state-title">{p("couldNotLoad")}</div>
          {token && (
            <button type="button" className="wosh-od__retry" onClick={load}>
              {p("retry")}
            </button>
          )}
        </div>
      ) : state === "success" ? (
        <div className="wosh-inv__card wosh-inv__card--center">
          <span className="wosh-inv__success-icon">
            <Icon name="checkmark-sharp" />
          </span>
          <div className="wosh-inv__big-title">{p("invoiceRequestedTitle")}</div>
          <div className="wosh-inv__body">{p("invoiceRequestedBody")}</div>
          {onDone && (
            <button type="button" className="wosh-inv__cta wosh-inv__cta--narrow" onClick={onDone}>
              {p("done")}
            </button>
          )}
        </div>
      ) : state === "generated" ? (
        <div className="wosh-inv__card">
          <span className="wosh-inv__doc-icon">
            <Icon name="document-text-outline" />
          </span>
          <div>
            <div className="wosh-inv__big-title">{p("alreadyExistsTitle")}</div>
            <div className="wosh-inv__body">{p("alreadyExistsBody")}</div>
          </div>
          {invoiceRef && (
            <div className="wosh-inv__ref">
              <span>{p("invoiceRef")}</span>
              <strong>{invoiceRef}</strong>
            </div>
          )}
          {onDone && (
            <button type="button" className="wosh-inv__cta" onClick={onDone}>
              {p("done")}
            </button>
          )}
        </div>
      ) : state === "unpaid" ? (
        <div className="wosh-inv__unpaid" role="status">
          <span className="wosh-inv__unpaid-icon">
            <Icon name="warning-outline" />
          </span>
          <div>
            <div className="wosh-inv__unpaid-title">{p("paymentPendingTitle")}</div>
            <div className="wosh-inv__unpaid-body">{p("paymentPendingBody")}</div>
          </div>
        </div>
      ) : (
        <form className="wosh-inv__form" onSubmit={submit} noValidate>
          {attempted && hasErrors(errors) && (
            <div className="wosh-inv__alert" role="alert">
              <Icon name="alert-circle" />
              {p("fixErrors")}
            </div>
          )}
          {submitError && (
            <div className="wosh-inv__alert" role="alert">
              <Icon name="alert-circle" />
              {submitError}
            </div>
          )}

          <div className="wosh-inv__card">
            <div className="wosh-inv__section">{p("billingInfo")}</div>
            {field("company", p("companyName"), { placeholder: "ACME SA", autoComplete: "organization" })}
            {field("vat", p("vatNumber"), {
              placeholder: "BE0123456789",
              autoCapitalize: "characters",
              // Forme canonique en quittant le champ : ce qui s'affiche est ce qui part.
              onBlur: () => {
                touch("vat")();
                if (isValidBelgianVat(form.vat)) {
                  setForm((current) => ({ ...current, vat: normalizeBelgianVat(current.vat) }));
                }
              },
            })}
            {field("email", p("billingEmail"), {
              type: "email",
              placeholder: "billing@company.com",
              autoComplete: "email",
            })}
            <label className="wosh-inv__field" htmlFor="wosh-inv-address">
              <span className="wosh-inv__label">{p("invoicingAddress")}</span>
              <input
                id="wosh-inv-address"
                className="wosh-inv__input"
                value={form.address}
                onChange={set("address")}
                placeholder="Rue de la Loi 1, 1000 Brussels"
                autoComplete="street-address"
              />
            </label>
            <div className="wosh-inv__hint">{p("hint")}</div>
          </div>

          <div className="wosh-inv__notice">
            <Icon name="information-circle-outline" />
            <span>{p("businessOnlyBody")}</span>
          </div>

          <button type="submit" className="wosh-inv__cta" disabled={submitting}>
            {submitting && <span className="wosh-po__spin wosh-po__spin--light" />}
            {submitting ? p("generating") : p("requestInvoice")}
          </button>
        </form>
      )}
    </div>
  );
}

/** Page publique /invoice/:token : le mot-symbole WOSH, sans navigation. */
function PublicInvoice({ authUser }) {
  const { token } = useParams();
  return (
    <div className="wosh-ord wosh-inv-page">
      <Sprite />
      <div className="wosh-inv-page__top">
        <span className="wosh-inv-page__brand">WOSH</span>
      </div>
      <div className="wosh-inv-page__body">
        <InvoiceRequest token={token} authUser={authUser} />
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  authUser: state?.auth?.isAuthenticated ? state?.auth?.user : null,
});

export default connect(mapStateToProps)(PublicInvoice);
